import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { BookingType, Rate } from '@/lib/data/services';

/**
 * The companion directory.
 *
 * Sourced from `v_browse_services` — the same rows a companion edits at
 * /companion/services and the same rows booking charges against.
 *
 * It used to read `chat_rate_coins` / `audio_rate_coins` / `video_rate_coins`
 * and the free-text `specialties` array from `companion_profiles`. Those
 * columns predate the pivot and nothing writes to them any more, so every card
 * showed a price of zero and the specialty filter was permanently empty. A
 * directory quoting prices nobody is charged is worse than no directory.
 */
export interface CompanionService {
  categorySlug: string;
  categoryName: string;
  rates: Rate[];
}

export interface Companion {
  id: string;
  name: string;
  avatarInitials: string;
  avatarHue: number;
  avatarUrl: string | null;
  headline: string;
  bio: string;
  languages: string[];
  city: string;
  citySlug: string | null;
  isOnline: boolean;
  isBusy: boolean;
  rating: number;
  ratingCount: number;
  totalMinutes: number;
  verified: boolean;
  /** Everything they offer, with real per-channel prices. */
  services: CompanionService[];
  /** The cheapest way to book them, for the "from X" line on a card. */
  cheapest: Rate | null;
  photoPosition?: string;
}

interface ProfileRow {
  id: string;
  headline: string | null;
  bio: string | null;
  rating_avg: number;
  rating_count: number;
  total_minutes: number;
  presence: 'offline' | 'online' | 'busy' | null;
  profiles: {
    display_name: string;
    languages: string[] | null;
    avatar_url: string | null;
    cities: { name: string; slug: string } | null;
  } | null;
}

interface ServiceRow {
  companion_id: string;
  category_slug: string;
  category_name: string;
  rates: { booking_type: BookingType; price_coins: number; unit: Rate['unit'] }[] | null;
}

const PROFILE_SELECT =
  'id, headline, bio, rating_avg, rating_count, total_minutes, presence, ' +
  'profiles!companion_profiles_id_fkey(display_name, languages, avatar_url, cities(name, slug))';

export function hueFor(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) % 360;
  return h;
}

export function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

function toCompanion(row: ProfileRow, services: CompanionService[]): Companion {
  const name = row.profiles?.display_name?.trim() || 'Companion';
  const allRates = services.flatMap((s) => s.rates);

  return {
    id: row.id,
    name,
    avatarInitials: initialsFor(name) || 'C',
    avatarHue: hueFor(row.id),
    avatarUrl: row.profiles?.avatar_url ?? null,
    headline: row.headline ?? '',
    bio: row.bio ?? '',
    languages: row.profiles?.languages ?? [],
    city: row.profiles?.cities?.name ?? '',
    citySlug: row.profiles?.cities?.slug ?? null,
    isOnline: row.presence === 'online',
    isBusy: row.presence === 'busy',
    rating: Number(row.rating_avg) || 0,
    ratingCount: row.rating_count,
    totalMinutes: row.total_minutes,
    verified: true, // only approved companions are returned at all
    services,
    cheapest:
      allRates.length === 0
        ? null
        : allRates.reduce((a, b) => (b.priceCoins < a.priceCoins ? b : a)),
  };
}

/** Group listings by the companion who offers them. */
function groupServices(rows: ServiceRow[]): Map<string, CompanionService[]> {
  const byCompanion = new Map<string, CompanionService[]>();

  for (const row of rows) {
    const list = byCompanion.get(row.companion_id) ?? [];
    list.push({
      categorySlug: row.category_slug,
      categoryName: row.category_name,
      rates: (row.rates ?? []).map((r) => ({
        bookingType: r.booking_type,
        priceCoins: r.price_coins,
        unit: r.unit,
      })),
    });
    byCompanion.set(row.companion_id, list);
  }

  return byCompanion;
}

export function useCompanions() {
  const [companions, setCompanions] = useState<Companion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    // Two queries rather than one: the view is per-SERVICE, and a card is per
    // PERSON. Joining them client-side is simpler than a view that would have
    // to aggregate services inside an aggregate of rates.
    void Promise.all([
      supabase
        .from('companion_profiles')
        .select(PROFILE_SELECT)
        .eq('status', 'approved')
        .order('rating_avg', { ascending: false }),
      supabase
        .from('v_browse_services')
        .select('companion_id, category_slug, category_name, rates'),
    ]).then(([profiles, services]) => {
      if (!active) return;

      if (profiles.error) {
        setError(profiles.error.message);
        setCompanions([]);
        setLoading(false);
        return;
      }

      const grouped = groupServices((services.data as unknown as ServiceRow[]) ?? []);

      setError(null);
      setCompanions(
        (profiles.data as unknown as ProfileRow[]).map((p) =>
          toCompanion(p, grouped.get(p.id) ?? []),
        ),
      );
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  return { companions, loading, error };
}

export function useCompanion(id: string | undefined) {
  const [companion, setCompanion] = useState<Companion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let active = true;

    void Promise.all([
      supabase
        .from('companion_profiles')
        .select(PROFILE_SELECT)
        .eq('status', 'approved')
        .eq('id', id)
        .maybeSingle(),
      supabase
        .from('v_browse_services')
        .select('companion_id, category_slug, category_name, rates')
        .eq('companion_id', id),
    ]).then(([profile, services]) => {
      if (!active) return;

      if (profile.error) {
        setError(profile.error.message);
      } else if (profile.data) {
        const grouped = groupServices((services.data as unknown as ServiceRow[]) ?? []);
        const row = profile.data as unknown as ProfileRow;
        setCompanion(toCompanion(row, grouped.get(row.id) ?? []));
      }
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [id]);

  return { companion, loading, error };
}

/**
 * Filter options built from whoever is actually in the directory.
 *
 * Services replace the old free-text `specialties`: a fixed taxonomy filters
 * reliably, where free text produced a dropdown of near-duplicates.
 */
export function facetsOf(companions: Companion[]) {
  const services = new Map<string, string>();
  for (const c of companions) {
    for (const s of c.services) services.set(s.categorySlug, s.categoryName);
  }

  return {
    languages: [...new Set(companions.flatMap((c) => c.languages))].sort(),
    cities: [...new Set(companions.map((c) => c.city).filter(Boolean))].sort(),
    services: [...services.entries()]
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}
